/**
 * Engine timer wrapping THREE.Timer for frame timing and fixed-step accumulation.
 */

import * as THREE from 'three/webgpu';
import { FIXED_DT, MAX_FIXED_STEPS } from '../shared/constants.js';

export class EngineTimer {
  private timer = new THREE.Timer();
  private _frame = 0;
  private _accumulator = 0;
  private _alpha = 0;
  private _timeScale = 1;
  private _clampedDt = 0;

  constructor() {
    // r174+: connect to document for automatic pause on tab-hidden
    if (typeof document !== 'undefined') {
      (this.timer as any).connect?.(document);
    }
  }

  /** Returns the clamped delta time from the last tick() call. */
  get dt(): number {
    return this._clampedDt;
  }

  get fixedDt(): number {
    return FIXED_DT;
  }

  get elapsed(): number {
    return this.timer.getElapsed();
  }

  get frame(): number {
    return this._frame;
  }

  get timeScale(): number {
    return this._timeScale;
  }

  set timeScale(value: number) {
    this._timeScale = Math.max(0, value);
  }

  get alpha(): number {
    return this._alpha;
  }

  /**
   * Called once per frame. Returns the number of fixed steps to run.
   */
  tick(): { rawDt: number; fixedSteps: number } {
    this.timer.update();
    this._frame++;

    const rawDt = this.timer.getDelta() * this._timeScale;
    const clampedDt = Math.min(rawDt, MAX_FIXED_STEPS * FIXED_DT);
    this._clampedDt = clampedDt;
    this._accumulator += clampedDt;

    let fixedSteps = 0;
    while (this._accumulator >= FIXED_DT) {
      this._accumulator -= FIXED_DT;
      fixedSteps++;
    }

    this._alpha = this._accumulator / FIXED_DT;

    return { rawDt, fixedSteps };
  }

  dispose(): void {
    // Disconnect the visibilitychange listener registered by connect(document)
    if (typeof document !== 'undefined') {
      (this.timer as any).disconnect?.();
    }
    (this.timer as any).dispose?.();
  }
}
